/** @format */

import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Warning,
  TrendingDown,
  TrendingUp,
  Psychology,
  Shield,
  Bolt,
  ExpandMore,
  WorkOutline,
  AttachMoney,
  Timeline,
  SmartToy,
  Groups,
  Speed,
  TrendingFlat,
  WorkspacePremium,
  DomainDisabled,
} from "@mui/icons-material";

// Styling Tokens consistent with GetInsights theme
const STYLES = {
  colors: {
    primary: "#000088",
    secondary: "#262655",
    accent: "#4A3D9E",
    textPrimary: "#1A1A1A",
    textSecondary: "#555577",
    border: "#D5D9EB",
    borderLight: "#E3E6F0",
    background: "#F3FBFF",
    backgroundWhite: "#FFFFFF",
    warning: "#DD6B20",
    danger: "#E53E3E",
    success: "#28A745",
    info: "#3182CE",
  },
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
  borderRadius: {
    card: "24px",
    small: "14px",
  },
  boxShadow: {
    card: "0 4px 20px 0 rgba(0,0,0,0.06)",
    cardHover: "0 8px 30px 0 rgba(0,0,0,0.12)",
  },
};

// Urgency badge configuration
const URGENCY_CONFIG = {
  IMMEDIATE: { color: "#E53E3E", bg: "#FFF5F5", label: "Immediate Urgency" },
  WITHIN_6_MONTHS: { color: "#DD6B20", bg: "#FFFAF0", label: "Within 6 Months" },
  WITHIN_1_YEAR: { color: "#3182CE", bg: "#EBF8FF", label: "Within 1 Year" },
};

// Impact Severity badge config
const SEVERITY_CONFIG = {
  CRITICAL: { color: "#E53E3E", bg: "#FFF5F5", border: "#FEB2B2" },
  HIGH: { color: "#DD6B20", bg: "#FFFAF0", border: "#FEEBC8" },
  MEDIUM: { color: "#3182CE", bg: "#EBF8FF", border: "#BEE3F8" },
  LOW: { color: "#28A745", bg: "#F0FFF4", border: "#C6F6D5" },
};

// Metric Stat Card Component
const StatCard = ({ icon, label, value, subtext, gradient }) => (
  <Paper
    elevation={0}
    sx={{
      width: "100%",
      height: "100%",
      boxSizing: "border-box",
      background: gradient || "linear-gradient(135deg, #FFFFFF, #F8FAFC)",
      borderRadius: STYLES.borderRadius.small,
      p: { xs: 1.5, sm: 2.2 },
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.8,
      border: `1px solid ${STYLES.colors.borderLight}`,
      boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: STYLES.boxShadow.cardHover,
      },
    }}
  >
    <Box
      sx={{
        width: { xs: 36, sm: 44 },
        height: { xs: 36, sm: 44 },
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(255,255,255,0.9)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontFamily: STYLES.typography.fontFamily,
        fontSize: { xs: "15px", sm: "19px", md: "22px" },
        fontWeight: 800,
        color: STYLES.colors.secondary,
        lineHeight: 1.2,
        textAlign: "center",
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontFamily: STYLES.typography.fontFamily,
        fontSize: { xs: "10px", sm: "11px" },
        fontWeight: 600,
        color: STYLES.colors.textSecondary,
        textAlign: "center",
        lineHeight: 1.3,
      }}
    >
      {label}
    </Typography>
    {subtext && (
      <Typography
        sx={{
          fontFamily: STYLES.typography.fontFamily,
          fontSize: "9px",
          fontWeight: 600,
          color: STYLES.colors.textSecondary,
          textAlign: "center",
          opacity: 0.85,
        }}
      >
        {subtext}
      </Typography>
    )}
  </Paper>
);

// Main Component
function SkillAndRoleDisruptionSection({
  disruptionData,
  disruptionLoading,
  disruptionError,
  checkSkill,
}) {
  const [expandedDisruptionIndex, setExpandedDisruptionIndex] = useState(0); // First disruption open by default

  const handleDisruptionToggle = (index) => {
    setExpandedDisruptionIndex((prev) => (prev === index ? -1 : index));
  };

  // Only render section when we actually have valid output data (do not show skeleton while loading)
  if (disruptionLoading || !disruptionData || disruptionError) return null;

  // Extract structured properties safely
  const verdict = disruptionData?.redundancyVerdict || {};
  const isRedundant = verdict?.willAIMakeItRedundant === "YES";
  const automation = disruptionData?.automationBreakdown || {};
  const jobMarket = disruptionData?.jobMarketDisruption || {};
  const salary = disruptionData?.salaryDisruption || {};
  const majorDisruptions = disruptionData?.majorDisruptions || [];

  return (
    <Grid item xs={12}>
      <Box
        id="skill-role-disruption-section"
        sx={{
          border: "1.5px solid #000088",
          borderRadius: { xs: "24px", sm: "36px" },
          background: STYLES.colors.background,
          boxSizing: "border-box",
          overflow: "hidden",
          width: "100%",
        }}
        p={{ xs: 1.5, sm: 3, md: 4 }}
      >
        {/* Section Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: { xs: 2, sm: 3 },
          }}
        >
          <Psychology sx={{ fontSize: 36, color: "#805AD5" }} />
          <Typography
            color="primary"
            variant="h6"
            sx={{
              fontFamily: STYLES.typography.fontFamily,
              textAlign: "center",
              fontWeight: 700,
              color: STYLES.colors.primary,
            }}
          >
            Skill & Role Disruption for {checkSkill}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2.5, sm: 3.5 } }}>

            {/* 1. HERO REDUNDANCY VERDICT BANNER */}
            {verdict?.willAIMakeItRedundant && (
              <Paper
                elevation={0}
                sx={{
                  background: isRedundant
                    ? "linear-gradient(135deg, #FFF5F5 0%, #FED7D7 100%)"
                    : "linear-gradient(135deg, #F0FFF4 0%, #C6F6D5 100%)",
                  borderRadius: STYLES.borderRadius.card,
                  p: { xs: 2, sm: 3 },
                  border: `2px solid ${isRedundant ? "#FEB2B2" : "#9AE6B4"}`,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: { xs: 1.5, sm: 2 } }}>
                  <Box
                    sx={{
                      width: { xs: 44, sm: 56 },
                      height: { xs: 44, sm: 56 },
                      borderRadius: "16px",
                      background: isRedundant ? "#E53E3E" : "#28A745",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#FFFFFF",
                      flexShrink: 0,
                    }}
                  >
                    {isRedundant ? (
                      <Warning sx={{ fontSize: { xs: 24, sm: 32 } }} />
                    ) : (
                      <Shield sx={{ fontSize: { xs: 24, sm: 32 } }} />
                    )}
                  </Box>
                  <Box sx={{ width: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                      <Typography
                        sx={{
                          fontFamily: STYLES.typography.fontFamily,
                          fontSize: { xs: "11px", sm: "12px" },
                          fontWeight: 700,
                          textTransform: "uppercase",
                          color: isRedundant ? "#C53030" : "#22543D",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Will AI Make {checkSkill} Redundant?
                      </Typography>
                      <Chip
                        label={`${verdict.willAIMakeItRedundant} (${verdict.confidenceLevel || "HIGH"} Confidence)`}
                        size="small"
                        sx={{
                          fontFamily: STYLES.typography.fontFamily,
                          fontWeight: 800,
                          fontSize: { xs: "10px", sm: "11px" },
                          background: isRedundant ? "#E53E3E" : "#28A745",
                          color: "#FFFFFF",
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: STYLES.typography.fontFamily,
                        fontSize: { xs: "13px", sm: "15px", md: "16px" },
                        fontWeight: 600,
                        color: STYLES.colors.textPrimary,
                        lineHeight: 1.5,
                      }}
                    >
                      "{verdict.oneLineReason}"
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* 2. KEY STATS METRICS GRID */}
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  icon={<Speed sx={{ color: STYLES.colors.danger, fontSize: { xs: 20, sm: 24 } }} />}
                  value={`${automation.overallAutomationRisk || 0}%`}
                  label="Task Automation Risk"
                  subtext="Automated by AI"
                  gradient="linear-gradient(135deg, #FFF5F5 0%, #FFFFFF 100%)"
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  icon={<Groups sx={{ color: STYLES.colors.warning, fontSize: { xs: 20, sm: 24 } }} />}
                  value={jobMarket.estimatedGlobalJobsAffected || "N/A"}
                  label="Global Jobs Impacted"
                  subtext={jobMarket.jobLossPercentageLast3Years ? `3Yr Loss: ${jobMarket.jobLossPercentageLast3Years}` : `Trend: ${jobMarket.jobLossTrend || "Accelerating"}`}
                  gradient="linear-gradient(135deg, #FFFAF0 0%, #FFFFFF 100%)"
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  icon={<WorkspacePremium sx={{ color: STYLES.colors.success, fontSize: { xs: 20, sm: 24 } }} />}
                  value={salary.salaryPremiumForAIAdoption || "N/A"}
                  label="AI Adoption Salary Premium"
                  subtext="For AI-Skilled Devs"
                  gradient="linear-gradient(135deg, #F0FFF4 0%, #FFFFFF 100%)"
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  icon={<TrendingUp sx={{ color: STYLES.colors.info, fontSize: { xs: 20, sm: 24 } }} />}
                  value={salary.seniorLevelImpact?.salaryChange || "N/A"}
                  label="Senior Level Pay Growth"
                  subtext={salary.salaryChangePercentage ? `Overall: ${salary.salaryChangePercentage}` : "High Strategic Value"}
                  gradient="linear-gradient(135deg, #EBF8FF 0%, #FFFFFF 100%)"
                />
              </Grid>
            </Grid>

            {/* 3. AUTOMATION BREAKDOWN: Easily Automated vs Human Edge */}
            <Paper
              elevation={0}
              sx={{
                background: STYLES.colors.backgroundWhite,
                borderRadius: STYLES.borderRadius.card,
                p: { xs: 2, sm: 3 },
                border: `1.5px solid ${STYLES.colors.borderLight}`,
              }}
            >
              <Typography
                sx={{
                  fontFamily: STYLES.typography.fontFamily,
                  fontSize: { xs: "16px", sm: "18px" },
                  fontWeight: 800,
                  color: STYLES.colors.secondary,
                  mb: 2.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <SmartToy sx={{ color: STYLES.colors.primary }} />
                AI Automation Breakdown for {checkSkill}
              </Typography>

              <Grid container spacing={3}>
                {/* Easily Automated Tasks */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      background: "#FFF5F5",
                      borderRadius: "18px",
                      p: { xs: 2, sm: 2.5 },
                      border: "1px solid #FED7D7",
                      height: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: STYLES.typography.fontFamily,
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "#C53030",
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Bolt sx={{ fontSize: 20 }} />
                      Tasks Being Automated by AI
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {automation.easilyAutomated?.map((item, idx) => {
                        const sev = SEVERITY_CONFIG[item.impactSeverity] || SEVERITY_CONFIG.MEDIUM;
                        return (
                          <Paper
                            key={idx}
                            elevation={0}
                            sx={{
                              p: 1.8,
                              borderRadius: "12px",
                              background: "#FFFFFF",
                              border: `1px solid ${sev.border}`,
                            }}
                          >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 0.5, mb: 0.8 }}>
                              <Typography
                                sx={{
                                  fontFamily: STYLES.typography.fontFamily,
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: STYLES.colors.textPrimary,
                                }}
                              >
                                {item.task}
                              </Typography>
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                {item.impactSeverity && (
                                  <Chip
                                    label={item.impactSeverity}
                                    size="small"
                                    sx={{
                                      fontFamily: STYLES.typography.fontFamily,
                                      fontSize: "9px",
                                      fontWeight: 800,
                                      background: sev.bg,
                                      color: sev.color,
                                      border: `1px solid ${sev.border}`,
                                    }}
                                  />
                                )}
                                <Chip
                                  label={`Accuracy ${item.automationAccuracy}`}
                                  size="small"
                                  sx={{
                                    fontFamily: STYLES.typography.fontFamily,
                                    fontSize: "9px",
                                    fontWeight: 700,
                                    background: "#FFF5F5",
                                    color: "#C53030",
                                  }}
                                />
                              </Box>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                              <Typography
                                sx={{
                                  fontFamily: STYLES.typography.fontFamily,
                                  fontSize: "10px",
                                  fontWeight: 600,
                                  color: STYLES.colors.textSecondary,
                                  mr: 0.5,
                                }}
                              >
                                Current AI Tools:
                              </Typography>
                              {item.currentAIToolsDoingThis?.map((tool, i) => (
                                <Chip
                                  key={i}
                                  label={tool}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontFamily: STYLES.typography.fontFamily,
                                    fontSize: "10px",
                                    height: "20px",
                                    borderColor: "#E2E8F0",
                                    background: "#F8FAFC",
                                  }}
                                />
                              ))}
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Box>
                </Grid>

                {/* Human Edge / Remains Untouched */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      background: "#F0FFF4",
                      borderRadius: "18px",
                      p: { xs: 2, sm: 2.5 },
                      border: "1px solid #C6F6D5",
                      height: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: STYLES.typography.fontFamily,
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "#22543D",
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Shield sx={{ fontSize: 20 }} />
                      Human Edge (Tasks Safe from AI)
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {automation.remainsUntouched?.map((item, idx) => {
                        const advantages = item.humanEdgeAdvantage
                          ? (Array.isArray(item.humanEdgeAdvantage) ? item.humanEdgeAdvantage : item.humanEdgeAdvantage.split(","))
                          : [];
                        return (
                          <Paper
                            key={idx}
                            elevation={0}
                            sx={{
                              p: 1.8,
                              borderRadius: "12px",
                              background: "#FFFFFF",
                              border: "1px solid #9AE6B4",
                            }}
                          >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 0.5, mb: 0.8 }}>
                              <Typography
                                sx={{
                                  fontFamily: STYLES.typography.fontFamily,
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: STYLES.colors.textPrimary,
                                }}
                              >
                                {item.task}
                              </Typography>
                              <Chip
                                label={`Stability: ${item.stabilityConfidence}`}
                                size="small"
                                sx={{
                                  fontFamily: STYLES.typography.fontFamily,
                                  fontSize: "9px",
                                  fontWeight: 800,
                                  background: "#C6F6D5",
                                  color: "#22543D",
                                }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                fontFamily: STYLES.typography.fontFamily,
                                fontSize: "12px",
                                color: STYLES.colors.textSecondary,
                                mb: 1,
                                lineHeight: 1.4,
                              }}
                            >
                              {item.whyAICantDoThis}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                              <Typography
                                sx={{
                                  fontFamily: STYLES.typography.fontFamily,
                                  fontSize: "10px",
                                  fontWeight: 600,
                                  color: "#2F855A",
                                  mr: 0.5,
                                }}
                              >
                                Human Advantage:
                              </Typography>
                              {advantages.map((adv, i) => (
                                <Chip
                                  key={i}
                                  label={adv.trim()}
                                  size="small"
                                  sx={{
                                    fontFamily: STYLES.typography.fontFamily,
                                    fontSize: "10px",
                                    height: "20px",
                                    background: "#E6FFFA",
                                    color: "#234E52",
                                    border: "1px solid #B2F5EA",
                                  }}
                                />
                              ))}
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* 4. SALARY DISRUPTION BY EXPERIENCE LEVEL */}
            <Paper
              elevation={0}
              sx={{
                background: STYLES.colors.backgroundWhite,
                borderRadius: STYLES.borderRadius.card,
                p: { xs: 2, sm: 3 },
                border: `1.5px solid ${STYLES.colors.borderLight}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
                <Typography
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    fontSize: { xs: "16px", sm: "18px" },
                    fontWeight: 800,
                    color: STYLES.colors.secondary,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <AttachMoney sx={{ color: STYLES.colors.success }} />
                  Salary Disruption by Experience Level
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {salary.salaryChangePercentage && (
                    <Chip
                      label={`Overall Range: ${salary.salaryChangePercentage}`}
                      size="small"
                      sx={{
                        fontFamily: STYLES.typography.fontFamily,
                        fontWeight: 700,
                        fontSize: "11px",
                        background: "#FAF5FF",
                        color: "#805AD5",
                      }}
                    />
                  )}
                  <Chip
                    label={`Impact: ${salary.averageSalaryImpact || "MIXED"}`}
                    size="small"
                    sx={{
                      fontFamily: STYLES.typography.fontFamily,
                      fontWeight: 700,
                      fontSize: "11px",
                      background: "#EBF8FF",
                      color: "#2B6CB0",
                    }}
                  />
                </Box>
              </Box>

              <Grid container spacing={2}>
                {/* Entry Level */}
                <Grid item xs={12} sm={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "16px",
                      background: "linear-gradient(135deg, #FFF5F5, #FFFFFF)",
                      border: "1.5px solid #FED7D7",
                      height: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography sx={{ fontFamily: STYLES.typography.fontFamily, fontSize: "14px", fontWeight: 700, color: STYLES.colors.textPrimary }}>
                        Entry Level
                      </Typography>
                      <Chip
                        icon={<TrendingDown sx={{ fontSize: 16 }} />}
                        label={salary.entryLevelImpact?.salaryChange || "Declining"}
                        size="small"
                        sx={{
                          fontFamily: STYLES.typography.fontFamily,
                          fontSize: "11px",
                          fontWeight: 800,
                          background: "#FED7D7",
                          color: "#C53030",
                          "& .MuiChip-icon": { color: "#C53030" },
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontFamily: STYLES.typography.fontFamily, fontSize: "12px", color: STYLES.colors.textSecondary, lineHeight: 1.5 }}>
                      {salary.entryLevelImpact?.explanation}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Mid Level */}
                <Grid item xs={12} sm={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "16px",
                      background: "linear-gradient(135deg, #FFFAF0, #FFFFFF)",
                      border: "1.5px solid #FEEBC8",
                      height: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography sx={{ fontFamily: STYLES.typography.fontFamily, fontSize: "14px", fontWeight: 700, color: STYLES.colors.textPrimary }}>
                        Mid Level
                      </Typography>
                      <Chip
                        icon={<TrendingFlat sx={{ fontSize: 16 }} />}
                        label={salary.midLevelImpact?.salaryChange || "Stable"}
                        size="small"
                        sx={{
                          fontFamily: STYLES.typography.fontFamily,
                          fontSize: "11px",
                          fontWeight: 800,
                          background: "#FEEBC8",
                          color: "#DD6B20",
                          "& .MuiChip-icon": { color: "#DD6B20" },
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontFamily: STYLES.typography.fontFamily, fontSize: "12px", color: STYLES.colors.textSecondary, lineHeight: 1.5 }}>
                      {salary.midLevelImpact?.explanation}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Senior Level */}
                <Grid item xs={12} sm={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "16px",
                      background: "linear-gradient(135deg, #F0FFF4, #FFFFFF)",
                      border: "1.5px solid #C6F6D5",
                      height: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography sx={{ fontFamily: STYLES.typography.fontFamily, fontSize: "14px", fontWeight: 700, color: STYLES.colors.textPrimary }}>
                        Senior Level
                      </Typography>
                      <Chip
                        icon={<TrendingUp sx={{ fontSize: 16 }} />}
                        label={salary.seniorLevelImpact?.salaryChange || "Growing"}
                        size="small"
                        sx={{
                          fontFamily: STYLES.typography.fontFamily,
                          fontSize: "11px",
                          fontWeight: 800,
                          background: "#C6F6D5",
                          color: "#22543D",
                          "& .MuiChip-icon": { color: "#22543D" },
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontFamily: STYLES.typography.fontFamily, fontSize: "12px", color: STYLES.colors.textSecondary, lineHeight: 1.5 }}>
                      {salary.seniorLevelImpact?.explanation}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>

            {/* 5. NEW AI ROLES CREATED & MAJOR DISRUPTIONS */}
            <Grid container spacing={3}>
              {/* New Roles Created */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    background: STYLES.colors.backgroundWhite,
                    borderRadius: STYLES.borderRadius.card,
                    p: { xs: 2.5, sm: 3 },
                    border: `1.5px solid ${STYLES.colors.borderLight}`,
                    height: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: STYLES.typography.fontFamily,
                      fontSize: "16px",
                      fontWeight: 800,
                      color: STYLES.colors.secondary,
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <WorkOutline sx={{ color: STYLES.colors.primary }} />
                    New AI-Driven Roles Created
                  </Typography>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {jobMarket.newRolesCreatedByAI?.map((role, idx) => (
                      <Paper
                        key={idx}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "14px",
                          background: "#F8FAFC",
                          border: "1px solid #E2E8F0",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                          <Typography
                            sx={{
                              fontFamily: STYLES.typography.fontFamily,
                              fontSize: "13px",
                              fontWeight: 700,
                              color: STYLES.colors.textPrimary,
                            }}
                          >
                            {role.roleName}
                          </Typography>
                          <Chip
                            label={role.demandLevel ? `${role.demandLevel} Demand` : "HIGH"}
                            size="small"
                            sx={{
                              fontFamily: STYLES.typography.fontFamily,
                              fontSize: "10px",
                              fontWeight: 700,
                              background: role.demandLevel === "HIGH" ? "#EBF8FF" : "#FEEBC8",
                              color: role.demandLevel === "HIGH" ? "#2B6CB0" : "#DD6B20",
                            }}
                          />
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Paper>
              </Grid>

              {/* Major Disruptions Timeline */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    background: STYLES.colors.backgroundWhite,
                    borderRadius: STYLES.borderRadius.card,
                    p: { xs: 2.5, sm: 3 },
                    border: `1.5px solid ${STYLES.colors.borderLight}`,
                    height: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: STYLES.typography.fontFamily,
                      fontSize: "16px",
                      fontWeight: 800,
                      color: STYLES.colors.secondary,
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Timeline sx={{ color: STYLES.colors.danger }} />
                    Major Industry Disruptions
                  </Typography>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {majorDisruptions.map((item, idx) => {
                      const sev = SEVERITY_CONFIG[item.impactLevel] || SEVERITY_CONFIG.HIGH;
                      const isExpanded = expandedDisruptionIndex === idx;
                      return (
                        <Accordion
                          key={idx}
                          elevation={0}
                          expanded={isExpanded}
                          onChange={() => handleDisruptionToggle(idx)}
                          disableGutters
                          sx={{
                            border: isExpanded
                              ? `1.5px solid ${STYLES.colors.accent}`
                              : "1px solid #E2E8F0",
                            borderRadius: "14px !important",
                            transition: "all 0.2s ease-in-out",
                            boxShadow: isExpanded
                              ? STYLES.boxShadow.cardHover
                              : "none",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1, flexWrap: "wrap", gap: 0.5 }}>
                              <Typography
                                sx={{
                                  fontFamily: STYLES.typography.fontFamily,
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: STYLES.colors.textPrimary,
                                }}
                              >
                                {item.disruptionTitle}
                              </Typography>
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                {item.impactLevel && (
                                  <Chip
                                    label={item.impactLevel}
                                    size="small"
                                    sx={{
                                      fontFamily: STYLES.typography.fontFamily,
                                      fontSize: "9px",
                                      fontWeight: 800,
                                      background: sev.bg,
                                      color: sev.color,
                                      border: `1px solid ${sev.border}`,
                                    }}
                                  />
                                )}
                                <Chip
                                  label={item.timeframe}
                                  size="small"
                                  sx={{
                                    fontFamily: STYLES.typography.fontFamily,
                                    fontSize: "9px",
                                    fontWeight: 700,
                                    background: "#FAF5FF",
                                    color: "#805AD5",
                                  }}
                                />
                              </Box>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography
                              sx={{
                                fontFamily: STYLES.typography.fontFamily,
                                fontSize: "12px",
                                color: STYLES.colors.textSecondary,
                                mb: 1.5,
                                lineHeight: 1.5,
                              }}
                            >
                              {item.description}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                              <Typography sx={{ fontFamily: STYLES.typography.fontFamily, fontSize: "10px", fontWeight: 600, color: STYLES.colors.textSecondary }}>
                                Impacted Areas:
                              </Typography>
                              {item.affectedAreas?.map((area, i) => (
                                <Chip
                                  key={i}
                                  label={area}
                                  size="small"
                                  sx={{
                                    fontFamily: STYLES.typography.fontFamily,
                                    fontSize: "10px",
                                    height: "20px",
                                    background: "#FFF5F5",
                                    color: "#C53030",
                                  }}
                                />
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* 6. MAJOR TECH LAYOFFS & WORKFORCE REALIGNMENT */}
            {jobMarket.majorLayoffExamples && jobMarket.majorLayoffExamples.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  background: STYLES.colors.backgroundWhite,
                  borderRadius: STYLES.borderRadius.card,
                  p: { xs: 2, sm: 3 },
                  border: `1.5px solid ${STYLES.colors.borderLight}`,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    fontSize: { xs: "16px", sm: "18px" },
                    fontWeight: 800,
                    color: STYLES.colors.secondary,
                    mb: 2.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <DomainDisabled sx={{ color: STYLES.colors.danger }} />
                  Major Tech Layoffs & AI Workforce Shifts
                </Typography>

                <Grid container spacing={2}>
                  {jobMarket.majorLayoffExamples.map((layoff, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "14px",
                          background: "linear-gradient(135deg, #FFF5F5 0%, #FFFFFF 100%)",
                          border: "1px solid #FED7D7",
                          height: "100%",
                          boxSizing: "border-box",
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                          <Typography
                            sx={{
                              fontFamily: STYLES.typography.fontFamily,
                              fontSize: "14px",
                              fontWeight: 700,
                              color: STYLES.colors.textPrimary,
                            }}
                          >
                            {layoff.company}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                            <Chip
                              label={layoff.year}
                              size="small"
                              sx={{
                                fontFamily: STYLES.typography.fontFamily,
                                fontSize: "10px",
                                fontWeight: 700,
                                background: "#EDF2F7",
                                color: "#4A5568",
                              }}
                            />
                            <Chip
                              label={`${layoff.jobsCut} Cut`}
                              size="small"
                              sx={{
                                fontFamily: STYLES.typography.fontFamily,
                                fontSize: "10px",
                                fontWeight: 800,
                                background: "#FED7D7",
                                color: "#C53030",
                              }}
                            />
                          </Box>
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: STYLES.typography.fontFamily,
                            fontSize: "12px",
                            color: STYLES.colors.textSecondary,
                            lineHeight: 1.5,
                          }}
                        >
                          {layoff.reason}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}



          </Box>
      </Box>
    </Grid>
  );
}

export default SkillAndRoleDisruptionSection;
